import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEnvelope, faSync, faCheck, faExclamationCircle, faStar, faCrown, faPlug } from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import client from '../api/client';
import {
    fetchMailboxes,
    fetchSyncJob,
    MAILBOX_LIST_TIMEOUT_MS,
    startFirstMailboxSyncIfNeeded,
    startMailboxGoogleOAuth,
    triggerMailboxSync,
    type MailboxConnectMode,
    type MailboxStatusItem,
    type MailboxSyncTriggerRequest,
    type SyncJob,
} from '../api/mailboxes';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { navigateTo } from '../utils/browser';
import {
    buildMailboxCompositeSummary,
    connectionDotClass,
    formatLastSuccessfulSync,
    getAutoSyncHealthWarning,
    mailboxStatusPillToneClasses,
    recoverableGuidanceAlertClasses,
    syncDotClass,
} from '../utils/mailboxStatusDisplay';
import {
    formatMailboxScopedStageMessage,
    getSyncFailureRecoveryFromJob,
    getRecentSliceHint,
    getSyncStageDisplay,
    isTerminalSyncStage,
    shouldShowFailedJobCallout,
} from '../utils/syncStageDisplay';
import MailboxLimitBlockedCallout from './MailboxLimitBlockedCallout';
import type { MailboxLimitDetail } from '../utils/mailboxLimitMessaging';
import {
    MAILBOX_LIMIT_CODE,
    parseMailboxLimitFromApiError,
    parseMailboxOAuthSearch,
    proactiveMailboxLimitMessage,
    reconnectBlockedByMailboxLimit,
    safeDecodeURIComponent,
} from '../utils/mailboxLimitMessaging';

interface ConnectGmailModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const MAILBOX_STATUS_TIMEOUT_ERROR =
    'Mailbox status took too long to load. You can still connect Gmail, or close this panel and try again.';
const MAILBOX_STATUS_UNAVAILABLE_ERROR =
    'Could not load mailbox status. You can still connect Gmail, or close this panel and try again.';

function isAxiosTimeoutError(err: { code?: string; message?: string } | null | undefined): boolean {
    const message = String(err?.message || '');
    return err?.code === 'ECONNABORTED' || /timeout of \d+ms exceeded/i.test(message);
}

function mailboxSettingsLoadError(err: any): string {
    if (isAxiosTimeoutError(err) || err?.code === 'ERR_NETWORK') {
        return isAxiosTimeoutError(err)
            ? MAILBOX_STATUS_TIMEOUT_ERROR
            : MAILBOX_STATUS_UNAVAILABLE_ERROR;
    }
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim() && !/timeout of \d+ms exceeded/i.test(detail)) {
        return detail;
    }
    const message = typeof err?.message === 'string' ? err.message.trim() : '';
    if (message && !/timeout of \d+ms exceeded/i.test(message) && message !== 'Network Error') {
        return message;
    }
    return MAILBOX_STATUS_UNAVAILABLE_ERROR;
}

function isGmailAddressHint(value: string): boolean {
    const trimmed = value.trim();
    const at = trimmed.indexOf('@');
    return at > 0 && trimmed.includes('.', at + 1);
}

type TrackedSyncLocalPhase = 'requesting' | 'already_syncing';

interface TrackedMailboxSync {
    syncJobId: string;
    job: SyncJob | null;
    localPhase?: TrackedSyncLocalPhase;
}

function seedTrackedSyncFromMailboxes(
    mailboxes: MailboxStatusItem[],
): Record<number, TrackedMailboxSync> {
    const seeded: Record<number, TrackedMailboxSync> = {};
    for (const mailbox of mailboxes) {
        const job = mailbox.active_sync_job;
        if (job && !isTerminalSyncStage(job.current_stage)) {
            seeded[mailbox.id] = { syncJobId: job.id, job };
        }
    }
    return seeded;
}

function pruneTrackedSyncAfterRefresh(
    prev: Record<number, TrackedMailboxSync>,
    mailboxes: MailboxStatusItem[],
): Record<number, TrackedMailboxSync> {
    const next = { ...prev };
    for (const mailbox of mailboxes) {
        const local = next[mailbox.id];
        if (!local) {
            continue;
        }
        const job = mailbox.active_sync_job;
        if (job) {
            if (isTerminalSyncStage(job.current_stage) && job.id === local.syncJobId) {
                delete next[mailbox.id];
            }
            continue;
        }
        if (local.syncJobId) {
            continue;
        }
        const serverInProgress =
            mailbox.sync_in_progress ||
            String(mailbox.sync_status || '').toUpperCase() === 'IN_PROGRESS';
        if (!serverInProgress && local.localPhase !== 'requesting') {
            delete next[mailbox.id];
        }
    }
    return next;
}

function resolveRowSyncJob(
    mailbox: MailboxStatusItem,
    tracked?: TrackedMailboxSync,
): { job: SyncJob | null; localPhase?: TrackedSyncLocalPhase } {
    if (tracked?.job) {
        return { job: tracked.job, localPhase: tracked.localPhase };
    }
    if (tracked?.localPhase === 'requesting') {
        return { job: null, localPhase: 'requesting' };
    }
    if (mailbox.active_sync_job) {
        return { job: mailbox.active_sync_job };
    }
    if (
        mailbox.last_sync_job &&
        shouldShowFailedJobCallout(mailbox.last_sync_job, mailbox)
    ) {
        return { job: mailbox.last_sync_job };
    }
    return { job: tracked?.job ?? null, localPhase: tracked?.localPhase };
}

function isTrackedSyncInProgress(
    tracked: TrackedMailboxSync | undefined,
    mailbox: MailboxStatusItem,
): boolean {
    if (tracked?.localPhase === 'requesting') {
        return true;
    }
    const resolved = resolveRowSyncJob(mailbox, tracked);
    if (resolved.localPhase === 'requesting') {
        return true;
    }
    if (resolved.job && !isTerminalSyncStage(resolved.job.current_stage)) {
        return true;
    }
    if (resolved.job?.current_stage === 'failed') {
        return false;
    }
    if (tracked?.syncJobId && !resolved.job) {
        return true;
    }
    return Boolean(
        mailbox.sync_in_progress ||
            (mailbox.active_sync_job &&
                !isTerminalSyncStage(mailbox.active_sync_job.current_stage)),
    );
}

function liveStatusForTrackedSync(
    trackedByMailbox: Record<number, TrackedMailboxSync>,
    mailboxes: MailboxStatusItem[],
): string {
    const entries = Object.entries(trackedByMailbox);
    if (entries.length === 0) {
        return '';
    }
    const [mailboxIdStr, tracked] = entries[entries.length - 1];
    const mailbox = mailboxes.find((m) => m.id === Number(mailboxIdStr));
    const emailAddress = mailbox?.email_address ?? `mailbox ${mailboxIdStr}`;
    if (tracked.localPhase === 'requesting') {
        const display = getSyncStageDisplay('requested');
        return formatMailboxScopedStageMessage(emailAddress, display);
    }
    if (tracked.localPhase === 'already_syncing' && !tracked.job) {
        return `Already syncing ${emailAddress}.`;
    }
    const resolved = mailbox ? resolveRowSyncJob(mailbox, tracked) : { job: tracked.job };
    const stage =
        resolved.job?.current_stage ??
        (resolved.localPhase === 'requesting' || (tracked.syncJobId && !resolved.job)
            ? 'requested'
            : null);
    const display = getSyncStageDisplay(stage, resolved.job);
    return formatMailboxScopedStageMessage(emailAddress, display);
}

/** Per-mailbox sync stage detail — supplements MailboxStatusBadge, never replaces it. */
function MailboxSyncStagePanel({
    mailbox,
    tracked,
    onRetrySync,
    onReconnect,
    retryDisabled,
    reconnectBlockedByLimit,
}: {
    mailbox: MailboxStatusItem;
    tracked?: TrackedMailboxSync;
    onRetrySync: (mailboxId: number) => void;
    onReconnect: () => void;
    retryDisabled: boolean;
    reconnectBlockedByLimit?: boolean;
}) {
    const resolved = resolveRowSyncJob(mailbox, tracked);
    const stage = resolved.localPhase === 'requesting' ? 'requested' : resolved.job?.current_stage;
    const display = getSyncStageDisplay(stage, resolved.job);
    const recentSliceHint = getRecentSliceHint(resolved.job);
    const isFailed = resolved.job?.current_stage === 'failed';
    const showFailure =
        isFailed &&
        resolved.job &&
        shouldShowFailedJobCallout(resolved.job, mailbox);
    const recovery = showFailure
        ? getSyncFailureRecoveryFromJob(
              resolved.job!,
              mailbox.error_class,
              mailbox.connection_state,
          )
        : null;

    if (!tracked && !resolved.job && !resolved.localPhase) {
        if (
            mailbox.last_sync_job?.current_stage === 'complete' &&
            !mailbox.active_sync_job
        ) {
            const lastDisplay = getSyncStageDisplay('complete', mailbox.last_sync_job);
            return (
                <p className="text-xs text-theme-muted">{lastDisplay.label}</p>
            );
        }
        return null;
    }

    return (
        <div className="space-y-2">
            <p
                className={`text-xs flex items-center gap-1 ${
                    display.tone === 'success'
                        ? 'text-emerald-400'
                        : display.tone === 'danger'
                          ? 'text-red-400'
                          : 'text-electric-blue'
                }`}
            >
                {!display.isTerminal && (
                    <FontAwesomeIcon icon={faSync} spin className="text-[10px]" />
                )}
                <span>
                    {formatMailboxScopedStageMessage(mailbox.email_address, display)}
                </span>
            </p>
            {!showFailure && display.helperText && (
                <p className="text-[11px] text-theme-muted leading-snug">{display.helperText}</p>
            )}
            {!showFailure && recentSliceHint && (
                <p className="text-[11px] text-theme-muted leading-snug">{recentSliceHint}</p>
            )}
            {showFailure && recovery && (
                <div
                    role="alert"
                    className={`rounded-md border px-2 py-2 text-[11px] leading-snug space-y-2 ${recoverableGuidanceAlertClasses(recovery.tone)}`}
                >
                    <p className="font-medium">{recovery.headline}</p>
                    <p>{recovery.guidance}</p>
                    {recovery.secondaryDetail && (
                        <p className="opacity-80">{recovery.secondaryDetail}</p>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                        {recovery.primaryAction === 'reconnect' && (
                            reconnectBlockedByLimit ? (
                                <Link
                                    to="/app/billing"
                                    className="text-xs font-medium underline hover:no-underline"
                                    aria-label={`Upgrade to reconnect ${mailbox.email_address}`}
                                >
                                    Upgrade to reconnect
                                    <span className="sr-only"> {mailbox.email_address}</span>
                                </Link>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onReconnect}
                                    className="text-xs font-medium underline hover:no-underline"
                                    aria-label={`Reconnect ${mailbox.email_address}`}
                                >
                                    Reconnect Gmail
                                </button>
                            )
                        )}
                        {recovery.primaryAction === 'retry' && (
                            <button
                                type="button"
                                onClick={() => onRetrySync(mailbox.id)}
                                disabled={retryDisabled}
                                className="text-xs font-medium underline hover:no-underline disabled:opacity-50"
                                aria-label={`Retry sync for ${mailbox.email_address}`}
                            >
                                Retry Sync
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Separate connection vs sync health — never a single green “Connected” when sync failed. */
function MailboxStatusBadge({ mailbox }: { mailbox: MailboxStatusItem }) {
    const composite = buildMailboxCompositeSummary(mailbox);
    const conn = composite.connection;
    const sync = composite.syncHealth;
    const connPill = mailboxStatusPillToneClasses(conn.tone);
    const syncPill = mailboxStatusPillToneClasses(sync.tone);
    return (
        <div className="flex flex-col items-end gap-1 max-w-[min(100%,14rem)]">
            <div className="flex flex-wrap gap-1 justify-end" role="group" aria-label="Mailbox status">
                <span
                    title={conn.ariaLabel}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${connPill}`}
                >
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${connectionDotClass(conn.tone)}`} aria-hidden="true" />
                    {conn.label}
                </span>
                <span
                    title={sync.ariaLabel}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${syncPill}`}
                >
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${syncDotClass(sync.tone)}`} aria-hidden="true" />
                    {sync.label}
                </span>
            </div>
            {composite.guidance && (
                <div
                    role="alert"
                    className={`rounded-md border px-2 py-1 text-[11px] text-right leading-snug ${recoverableGuidanceAlertClasses(composite.guidance.tone)}`}
                >
                    {composite.guidance.nextStep}
                </div>
            )}
        </div>
    );
}

const ConnectGmailModal: React.FC<ConnectGmailModalProps> = ({ isOpen, onClose }) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const pollInFlightRef = useRef<Set<string>>(new Set());
    const { refresh: refreshBootstrap } = useAppBootstrap();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [autoSync, setAutoSync] = useState(false);
    const [mailboxLimitBlock, setMailboxLimitBlock] = useState<MailboxLimitDetail | null>(null);
    const [liveStatusMessage, setLiveStatusMessage] = useState('');
    const [trackedSyncByMailbox, setTrackedSyncByMailbox] = useState<
        Record<number, TrackedMailboxSync>
    >({});
    const trackedSyncRef = useRef(trackedSyncByMailbox);
    const [manualMailboxId, setManualMailboxId] = useState<number | null>(null);

    // Multi-mailbox state
    const [mailboxList, setMailboxList] = useState<MailboxStatusItem[]>([]);
    const mailboxListRef = useRef(mailboxList);
    const [maxMailboxes, setMaxMailboxes] = useState<number | null>(1);
    const [syncStatus, setSyncStatus] = useState<string>('IDLE');
    const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
    const [schedulerEnabled, setSchedulerEnabled] = useState<boolean | null>(null);
    const [lastAutoSyncAt, setLastAutoSyncAt] = useState<string | null>(null);
    const [addAnotherEmail, setAddAnotherEmail] = useState('');

    const connectedMailboxes = mailboxList.filter((m) => m.status === 'CONNECTED');
    const everyConnectedGrantExpired =
        connectedMailboxes.length > 0 &&
        connectedMailboxes.every((mailbox) => mailbox.error_class === 'oauth_expired');
    const hasAnyConnected = connectedMailboxes.length > 0;
    // Revoked/error mailboxes still render; reconnect stays behind the plan cap.
    const hasAnyMailboxes = mailboxList.length > 0;
    const canAddMore = maxMailboxes === null || connectedMailboxes.length < maxMailboxes;

    useEffect(() => {
        if (canAddMore) setMailboxLimitBlock(null);
    }, [canAddMore]);

    useEffect(() => {
        if (!manualMailboxId && connectedMailboxes.length > 0) {
            const primary =
                connectedMailboxes.find((m) => m.is_primary) ?? connectedMailboxes[0];
            setManualMailboxId(primary.id);
        }
    }, [connectedMailboxes, manualMailboxId]);

    const isMailboxSyncBlocked = useCallback(
        (mailboxId: number) => {
            const mailbox = mailboxList.find((m) => m.id === mailboxId);
            if (!mailbox) {
                return false;
            }
            if (mailbox.error_class === 'provider_sync_unavailable') {
                return true;
            }
            return isTrackedSyncInProgress(trackedSyncByMailbox[mailboxId], mailbox);
        },
        [mailboxList, trackedSyncByMailbox],
    );

    const anySyncing = useMemo(
        () => connectedMailboxes.some((m) => isMailboxSyncBlocked(m.id)),
        [connectedMailboxes, isMailboxSyncBlocked],
    );

    const autoSyncWarning = useMemo(
        () =>
            getAutoSyncHealthWarning({
                autoSyncEnabled: autoSync,
                hasConnectedMailbox: hasAnyConnected,
                schedulerEnabled,
                lastAutoSyncAt,
            }),
        [autoSync, hasAnyConnected, schedulerEnabled, lastAutoSyncAt],
    );

    useEffect(() => {
        trackedSyncRef.current = trackedSyncByMailbox;
    }, [trackedSyncByMailbox]);

    useEffect(() => {
        mailboxListRef.current = mailboxList;
    }, [mailboxList]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setLiveStatusMessage(liveStatusForTrackedSync(trackedSyncByMailbox, mailboxList));
    }, [isOpen, trackedSyncByMailbox, mailboxList]);

    // Move focus into the dialog on open so window triage shortcuts (useTriageNavigation) see activeElement inside role="dialog".
    useEffect(() => {
        if (!isOpen) return;
        const id = requestAnimationFrame(() => {
            panelRef.current?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(id);
    }, [isOpen]);

    const pollTrackedSyncJobs = useCallback(async () => {
        const entries = Object.entries(trackedSyncRef.current);
        await Promise.all(
            entries.map(async ([mailboxIdStr, tracked]) => {
                if (!tracked.syncJobId) {
                    return;
                }
                const mailboxId = Number(mailboxIdStr);
                const pollKey = `${mailboxId}:${tracked.syncJobId}`;
                if (pollInFlightRef.current.has(pollKey)) {
                    return;
                }
                pollInFlightRef.current.add(pollKey);
                try {
                    const job = await fetchSyncJob(mailboxId, tracked.syncJobId);
                    setTrackedSyncByMailbox((prev) => ({
                        ...prev,
                        [mailboxId]: {
                            ...prev[mailboxId],
                            syncJobId: tracked.syncJobId,
                            job,
                        },
                    }));
                    if (isTerminalSyncStage(job.current_stage)) {
                        setTrackedSyncByMailbox((prev) => {
                            const next = { ...prev };
                            delete next[mailboxId];
                            return next;
                        });
                    }
                } catch {
                    // Keep tracked state until the next mailbox list refresh succeeds.
                } finally {
                    pollInFlightRef.current.delete(pollKey);
                }
            }),
        );
    }, []);

    const checkConnectionStatus = useCallback(async () => {
        if (!anySyncing) setCheckingStatus(true);

        try {
            // Load independently: a hung /user/me used to discard a successful
            // /mailboxes response and blank the list as "timeout of 8000ms exceeded".
            const [mailboxesOutcome, userOutcome] = await Promise.allSettled([
                fetchMailboxes(),
                client.get('/user/me', { timeout: MAILBOX_LIST_TIMEOUT_MS }),
            ]);

            let list: MailboxStatusItem[] | null = null;
            if (mailboxesOutcome.status === 'fulfilled') {
                const mailboxesData = mailboxesOutcome.value;
                const nextList: MailboxStatusItem[] = Array.isArray(mailboxesData?.mailboxes)
                    ? mailboxesData.mailboxes
                    : [];
                list = nextList;
                setMailboxList(nextList);
                setTrackedSyncByMailbox((prev) => {
                    const pruned = pruneTrackedSyncAfterRefresh(prev, nextList);
                    const seeded = seedTrackedSyncFromMailboxes(nextList);
                    return { ...pruned, ...seeded };
                });
                setMaxMailboxes(
                    mailboxesData?.max_mailboxes !== undefined ? mailboxesData.max_mailboxes : 1,
                );
            } else {
                console.error('Failed to load mailboxes:', mailboxesOutcome.reason);
            }

            let userData: any = null;
            if (userOutcome.status === 'fulfilled') {
                userData = userOutcome.value.data;
                setAutoSync(userData.is_auto_sync_enabled || false);
                setSyncStatus(userData.sync_status || 'IDLE');
                setLastSyncedAt(userData.last_synced_at);
                setSchedulerEnabled(userData.sync_scheduler_enabled ?? null);
                setLastAutoSyncAt(userData.last_auto_sync_at ?? null);
            } else {
                console.error('Failed to load user sync settings:', userOutcome.reason);
            }

            if (mailboxesOutcome.status === 'rejected') {
                if (mailboxListRef.current.length > 0) {
                    return;
                }
                setError(mailboxSettingsLoadError(mailboxesOutcome.reason));
                return;
            }

            if (userOutcome.status === 'rejected') {
                // Mailboxes already rendered; do not hide them behind a raw axios timeout.
                return;
            }

            // User-level sync fields (GET /user/me) can lag per-mailbox rows after single-mailbox sync.
            // Only show the red banner when both the user row and at least one mailbox report failure.
            const userSyncFailed = userData.sync_status === 'FAILED' && Boolean(userData.sync_error);
            const hasMailboxLevelSyncFailure = (list ?? []).some(
                (m) =>
                    m.status === 'CONNECTED' &&
                    String(m.sync_status || '').toUpperCase() === 'FAILED',
            );
            setError((prev) => {
                if (
                    prev === MAILBOX_STATUS_UNAVAILABLE_ERROR ||
                    prev === MAILBOX_STATUS_TIMEOUT_ERROR
                ) {
                    return null;
                }
                if (userSyncFailed && hasMailboxLevelSyncFailure && userData.sync_error) {
                    return `Sync failed: ${userData.sync_error}`;
                }
                if (prev != null && prev.startsWith('Sync failed:')) {
                    return null;
                }
                return prev;
            });
        } finally {
            setCheckingStatus(false);
        }
    }, [anySyncing]);

    const refreshMailboxState = useCallback(async () => {
        await checkConnectionStatus();
        await refreshBootstrap();
    }, [checkConnectionStatus, refreshBootstrap]);

    const handleOAuthCallback = useCallback(async () => {
        const params = new URLSearchParams(window.location.search);
        const mailboxStatus = params.get('mailbox');

        if (mailboxStatus === 'success') {
            setSuccessMessage('Gmail connected successfully!');
            setError(null);
            setMailboxLimitBlock(null);
            await refreshMailboxState();
            try {
                const started = await startFirstMailboxSyncIfNeeded();
                if (started.length > 0) {
                    setSuccessMessage('Gmail connected. First sync started.');
                    await refreshMailboxState();
                }
            } catch {
                setSuccessMessage(
                    'Gmail connected. If mail does not appear shortly, use Start Sync.',
                );
            }
            window.history.replaceState({}, '', window.location.pathname);
        } else if (mailboxStatus === 'error') {
            const oauthParsed = parseMailboxOAuthSearch(window.location.search);
            setSuccessMessage(null);
            if (oauthParsed && 'code' in oauthParsed && oauthParsed.code === MAILBOX_LIMIT_CODE) {
                setMailboxLimitBlock(oauthParsed);
                setError(null);
            } else if (oauthParsed && 'genericMessage' in oauthParsed) {
                setMailboxLimitBlock(null);
                setError(oauthParsed.genericMessage);
            } else {
                setMailboxLimitBlock(null);
                const message = params.get('message') || 'Authorization failed';
                setError(safeDecodeURIComponent(message.replace(/\+/g, ' ')));
            }
            await refreshMailboxState();
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [refreshMailboxState]);

    useEffect(() => {
        if (isOpen) {
            void checkConnectionStatus();
            void handleOAuthCallback();
        }
    }, [isOpen, checkConnectionStatus, handleOAuthCallback]);

    const triggerMailboxSyncFlow = useCallback(
        async (mailboxId: number, options?: MailboxSyncTriggerRequest) => {
            setError(null);
            setSuccessMessage(null);
            setTrackedSyncByMailbox((prev) => ({
                ...prev,
                [mailboxId]: {
                    syncJobId: prev[mailboxId]?.syncJobId ?? '',
                    job: null,
                    localPhase: 'requesting',
                },
            }));

            try {
                const response = await triggerMailboxSync(mailboxId, options);
                const localPhase: TrackedSyncLocalPhase | undefined =
                    response.status === 'already_in_progress' ? 'already_syncing' : undefined;
                setTrackedSyncByMailbox((prev) => ({
                    ...prev,
                    [mailboxId]: {
                        syncJobId: response.sync_job_id,
                        job: null,
                        ...(localPhase ? { localPhase } : {}),
                    },
                }));
                await checkConnectionStatus();
                await refreshBootstrap();
            } catch (err: unknown) {
                setTrackedSyncByMailbox((prev) => {
                    const next = { ...prev };
                    delete next[mailboxId];
                    return next;
                });
                if (isAxiosTimeoutError(err as { code?: string; message?: string })) {
                    setError('Sync is taking longer than expected. Try Start Sync again.');
                    return;
                }
                const detail =
                    (err as { response?: { data?: { detail?: unknown } } })?.response?.data
                        ?.detail ?? 'Failed to trigger sync.';
                const asString =
                    typeof detail === 'string'
                        ? detail
                        : detail &&
                            typeof detail === 'object' &&
                            typeof (detail as { message?: string }).message === 'string'
                          ? (detail as { message: string }).message
                          : 'Failed to trigger sync.';
                setError(asString);
            }
        },
        [checkConnectionStatus, refreshBootstrap],
    );

    // Polling when any mailbox is syncing (2s cadence; extends existing mailbox refresh loop).
    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;
        if (isOpen && anySyncing) {
            interval = setInterval(() => {
                void checkConnectionStatus();
                void pollTrackedSyncJobs();
            }, 2000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isOpen, anySyncing, checkConnectionStatus, pollTrackedSyncJobs]);

    useEffect(() => {
        if (!isOpen) {
            setTrackedSyncByMailbox({});
            setLiveStatusMessage('');
            pollInFlightRef.current.clear();
        }
    }, [isOpen]);

    const handleGoogleLogin = async (
        connectMode: MailboxConnectMode,
        emailAddress?: string,
    ) => {
        if (connectMode === 'reconnect' && emailAddress) {
            const target = mailboxList.find((mailbox) => mailbox.email_address === emailAddress);
            if (target && reconnectBlockedByMailboxLimit(target, canAddMore)) {
                return;
            }
        }
        try {
            const response = await startMailboxGoogleOAuth({
                connectMode,
                emailAddress,
            });
            const url = response.url;
            if (!url) throw new Error('Missing Gmail authorization URL.');
            navigateTo(url);
        } catch (err: any) {
            const limitDetail = parseMailboxLimitFromApiError(err);
            if (limitDetail) {
                setMailboxLimitBlock(limitDetail);
                setError(null);
                return;
            }
            const detail = err?.response?.data?.detail;
            const asString =
                typeof detail === 'string'
                    ? detail
                    : detail && typeof detail === 'object' && typeof (detail as { message?: string }).message === 'string'
                      ? (detail as { message: string }).message
                      : 'Failed to start Gmail authorization.';
            setMailboxLimitBlock(null);
            setError(asString);
        }
    };

    const handleAddAnotherGmail = async () => {
        const hint = addAnotherEmail.trim();
        if (!isGmailAddressHint(hint)) {
            setError('Enter the Gmail address you want to connect, then continue.');
            return;
        }
        setError(null);
        await handleGoogleLogin('add', hint);
    };

    const handleDisconnect = async (mailboxId: number, emailAddress: string) => {
        const ok = window.confirm(
            `Disconnect ${emailAddress}? Other connected mailboxes stay linked.`
        );
        if (!ok) return;
        try {
            await client.post(`/mailboxes/${mailboxId}/disconnect`);
            setSuccessMessage('Mailbox disconnected.');
            setTimeout(() => setSuccessMessage(null), 3000);
            checkConnectionStatus();
            await refreshBootstrap();
        } catch {
            setError('Failed to disconnect. Please try again.');
        }
    };

    const handleSetPrimary = async (mailboxId: number) => {
        try {
            await client.post(`/mailboxes/${mailboxId}/set-primary`);
            setSuccessMessage('Primary mailbox updated.');
            setTimeout(() => setSuccessMessage(null), 3000);
            checkConnectionStatus();
            await refreshBootstrap();
        } catch {
            setError('Failed to set primary mailbox.');
        }
    };

    const handleSyncMailbox = async (mailboxId: number) => {
        await triggerMailboxSyncFlow(mailboxId);
    };

    const handleToggleAutoSync = async () => {
        const newValue = !autoSync;
        setAutoSync(newValue);
        try {
            await client.post('/user/settings', { is_auto_sync_enabled: newValue });
        } catch {
            setAutoSync(!newValue);
        }
    };

    const manualSyncTargetId =
        manualMailboxId ??
        connectedMailboxes.find((m) => m.is_primary)?.id ??
        connectedMailboxes[0]?.id ??
        null;
    const manualSyncTarget =
        manualSyncTargetId != null
            ? mailboxList.find((m) => m.id === manualSyncTargetId) ?? null
            : null;
    // Provider-backed mailboxes reject Gmail-syntax queries (the API returns
    // 400) — hide the field for them instead of letting the sync fail.
    const manualQuerySupported = manualSyncTarget?.supports_custom_query !== false;

    const handleSync = async (e: React.FormEvent) => {
        e.preventDefault();
        if (manualSyncTargetId == null) {
            setError('Connect a Gmail account before syncing.');
            return;
        }
        const trimmedQuery = manualQuerySupported ? searchQuery.trim() : '';
        await triggerMailboxSyncFlow(manualSyncTargetId, {
            start_date: startDate || null,
            end_date: endDate || null,
            query: trimmedQuery.length > 0 ? trimmedQuery : null,
        });
    };

    const manualSyncBlocked =
        manualMailboxId != null ? isMailboxSyncBlocked(manualMailboxId) : anySyncing;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

            <div
                ref={panelRef}
                tabIndex={-1}
                className="relative w-full max-w-md bg-theme-card border border-theme rounded-2xl shadow-2xl overflow-hidden fade-in-up max-h-[90vh] flex flex-col outline-none focus:outline-none"
                role="dialog"
                aria-modal="true"
                aria-labelledby="connect-gmail-modal-title"
            >
                {/* Header */}
                <div className="px-6 py-4 bg-theme-secondary border-b border-theme flex justify-between items-center shrink-0">
                    <h3 id="connect-gmail-modal-title" className="text-lg font-bold text-theme flex items-center gap-2">
                        <FontAwesomeIcon icon={faEnvelope} className="text-electric-blue" />
                        Gmail Settings
                    </h3>
                    <button onClick={onClose} className="text-theme-muted hover:text-theme transition-colors">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="p-6 space-y-5 overflow-y-auto scrollbar-thin">
                    <p
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        className={`text-sm text-theme-secondary flex items-center gap-2 min-h-[1.25rem] ${liveStatusMessage ? '' : 'sr-only'}`}
                    >
                        {liveStatusMessage ? (
                            <>
                                <FontAwesomeIcon icon={faSync} spin className="text-electric-blue" />
                                {liveStatusMessage}
                            </>
                        ) : (
                            'Gmail sync status updates appear here.'
                        )}
                    </p>

                    {/* Success Message */}
                    {successMessage && (
                        <div className="bg-green-400/10 text-green-400 p-3 rounded-lg border border-green-400/20 flex items-center gap-2 text-sm">
                            <FontAwesomeIcon icon={faCheck} />
                            {successMessage}
                        </div>
                    )}

                    {/* Error Message */}
                    {mailboxLimitBlock && (
                        <MailboxLimitBlockedCallout
                            body={mailboxLimitBlock.message}
                            detail={mailboxLimitBlock}
                            usageLine={
                                mailboxLimitBlock.limit != null && mailboxLimitBlock.connected != null
                                    ? `${mailboxLimitBlock.connected} of ${mailboxLimitBlock.limit} mailboxes connected on your plan`
                                    : mailboxLimitBlock.limit != null
                                      ? `Plan mailbox limit: ${mailboxLimitBlock.limit}`
                                      : null
                            }
                        />
                    )}
                    {error && (
                        <div className="bg-red-400/10 text-red-400 p-3 rounded-lg border border-red-400/20 flex items-center gap-2 text-sm">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            {error}
                        </div>
                    )}

                    {/* Loading State */}
                    {checkingStatus ? (
                        <div className="text-center py-8">
                            <FontAwesomeIcon icon={faSync} spin className="text-electric-blue text-2xl mb-2" />
                            <p className="text-gray-400 text-sm">Checking connection status...</p>
                        </div>
                    ) : (
                        <>
                            {/* === Mailbox List Section === */}
                            <div className="space-y-3">
                                <p className="text-theme font-semibold text-sm">
                                    Mailboxes
                                    {hasAnyMailboxes && (
                                        <span className="text-theme-muted font-normal ml-2">
                                            ({connectedMailboxes.length}{maxMailboxes !== null ? `/${maxMailboxes}` : ''})
                                        </span>
                                    )}
                                </p>

                                {!hasAnyMailboxes ? (
                                    /* No mailboxes — first connect CTA */
                                    <div className="text-center space-y-3 py-2">
                                        <p className="text-theme-secondary text-sm">
                                            Connect your Gmail account to sync emails automatically.
                                        </p>
                                        <button
                                            onClick={() => handleGoogleLogin('add')}
                                            className="w-full bg-theme text-theme border border-theme hover:bg-theme-secondary font-bold py-3 rounded-xl shadow-sm transition-colors flex items-center justify-center gap-3"
                                        >
                                            <FontAwesomeIcon icon={faGoogle} className="text-red-500 text-xl" />
                                            Connect Gmail
                                        </button>
                                    </div>
                                ) : (
                                    /* Mailbox list — includes revoked rows so they can be reconnected */
                                    <div className="space-y-2">
                                        {mailboxList
                                            .map((mailbox) => (
                                                <div
                                                    key={mailbox.id}
                                                    className="border border-theme rounded-lg p-3 space-y-2"
                                                >
                                                    {/* Row 1: Email + badges */}
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            {mailbox.is_primary && mailbox.status === 'CONNECTED' && (
                                                                <FontAwesomeIcon icon={faCrown} className="text-amber-400 text-xs shrink-0" title="Primary" />
                                                            )}
                                                            <span className="text-sm text-theme truncate">{mailbox.email_address}</span>
                                                        </div>
                                                        <MailboxStatusBadge mailbox={mailbox} />
                                                    </div>

                                                    {/* Row 2: Metadata */}
                                                    <div className="flex items-center gap-3 text-xs text-theme-muted">
                                                        {mailbox.email_count !== undefined && (
                                                            <span>{mailbox.email_count} emails</span>
                                                        )}
                                                        <span>
                                                            Last sync: {formatLastSuccessfulSync(mailbox.last_successful_sync ?? mailbox.last_synced_at)}
                                                        </span>
                                                        {mailbox.sync_error && (
                                                            <span className="text-red-400 truncate" title={mailbox.sync_error}>
                                                                {mailbox.sync_error}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <MailboxSyncStagePanel
                                                        mailbox={mailbox}
                                                        tracked={trackedSyncByMailbox[mailbox.id]}
                                                        onRetrySync={handleSyncMailbox}
                                                        onReconnect={() => handleGoogleLogin('reconnect', mailbox.email_address)}
                                                        retryDisabled={isMailboxSyncBlocked(mailbox.id)}
                                                        reconnectBlockedByLimit={reconnectBlockedByMailboxLimit(
                                                            mailbox,
                                                            canAddMore,
                                                        )}
                                                    />

                                                    {/* Row 3: Actions — disconnected rows reconnect only when under the plan cap */}
                                                    {(mailbox.status !== 'CONNECTED' || mailbox.error_class === 'oauth_expired') && (
                                                        <div className="flex items-center gap-3 pt-1">
                                                            {reconnectBlockedByMailboxLimit(mailbox, canAddMore) ? (
                                                                <Link
                                                                    to="/app/billing"
                                                                    className="text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                                                                    aria-label={`Upgrade to reconnect ${mailbox.email_address}`}
                                                                >
                                                                    Upgrade to reconnect
                                                                    <span className="sr-only"> {mailbox.email_address}</span>
                                                                </Link>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleGoogleLogin('reconnect', mailbox.email_address)}
                                                                    className="text-xs font-semibold text-electric-blue hover:text-blue-400 transition-colors flex items-center gap-1"
                                                                    aria-label={`Reconnect ${mailbox.email_address}`}
                                                                >
                                                                    <FontAwesomeIcon icon={faGoogle} className="text-[10px]" />
                                                                    Reconnect
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    {mailbox.status === 'CONNECTED' && (
                                                        <div className="flex items-center gap-3 pt-1">
                                                            {!mailbox.is_primary && (
                                                                <button
                                                                    onClick={() => handleSetPrimary(mailbox.id)}
                                                                    disabled={isMailboxSyncBlocked(mailbox.id)}
                                                                    className="text-xs text-theme-muted hover:text-amber-400 transition-colors flex items-center gap-1 disabled:opacity-50"
                                                                >
                                                                    <FontAwesomeIcon icon={faStar} className="text-[10px]" />
                                                                    Set Primary
                                                                </button>
                                                            )}
                                                            {mailbox.error_class !== 'oauth_expired' && (
                                                            <button
                                                                onClick={() => handleSyncMailbox(mailbox.id)}
                                                                disabled={isMailboxSyncBlocked(mailbox.id)}
                                                                className="text-xs text-theme-muted hover:text-electric-blue transition-colors flex items-center gap-1 disabled:opacity-50"
                                                            >
                                                                <FontAwesomeIcon icon={faSync} className={`text-[10px] ${isMailboxSyncBlocked(mailbox.id) ? 'animate-spin' : ''}`} />
                                                                Sync
                                                            </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleDisconnect(mailbox.id, mailbox.email_address)}
                                                                disabled={isMailboxSyncBlocked(mailbox.id)}
                                                                className="text-xs text-theme-muted hover:text-red-400 transition-colors flex items-center gap-1 disabled:opacity-50"
                                                            >
                                                                <FontAwesomeIcon icon={faPlug} className="text-[10px]" />
                                                                Disconnect
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                        {/* Connect Another Gmail */}
                                        {canAddMore ? (
                                            <div className="mt-1 space-y-2">
                                                <label className="block">
                                                    <span className="sr-only">Gmail address to connect</span>
                                                    <input
                                                        type="email"
                                                        value={addAnotherEmail}
                                                        onChange={(event) => setAddAnotherEmail(event.target.value)}
                                                        placeholder="Gmail address to connect"
                                                        autoComplete="email"
                                                        className="w-full bg-theme text-theme border border-theme rounded-lg px-3 py-2 text-sm"
                                                    />
                                                </label>
                                                <button
                                                    onClick={handleAddAnotherGmail}
                                                    className="w-full bg-theme text-theme border border-dashed border-theme hover:bg-theme-secondary py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                                                >
                                                    <FontAwesomeIcon icon={faGoogle} className="text-red-500" />
                                                    Connect Another Gmail
                                                </button>
                                            </div>
                                        ) : maxMailboxes != null ? (
                                            <div className="mt-1">
                                                <MailboxLimitBlockedCallout
                                                    body={proactiveMailboxLimitMessage(maxMailboxes, connectedMailboxes.length)}
                                                    usageLine={`${connectedMailboxes.length} of ${maxMailboxes} mailboxes connected on your plan`}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-theme"></div>

                            {/* Auto-Sync Toggle */}
                            <div className="flex items-center justify-between py-1">
                                <div>
                                    <p className="text-theme font-semibold text-sm">Auto-Sync (Hourly)</p>
                                    <p className="text-theme-muted text-xs">Automatically check for new emails</p>
                                </div>
                                <button
                                    onClick={handleToggleAutoSync}
                                    disabled={!hasAnyConnected}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${autoSync ? 'bg-green-500' : 'bg-gray-600'
                                        } ${!hasAnyConnected && 'opacity-50 cursor-not-allowed'}`}
                                >
                                    <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${autoSync ? 'translate-x-6' : 'translate-x-0'
                                        }`}></div>
                                </button>
                            </div>
                            {autoSyncWarning && (
                                <div
                                    role="alert"
                                    className={`rounded-md border px-3 py-2 text-xs leading-snug ${recoverableGuidanceAlertClasses('warning')}`}
                                >
                                    {autoSyncWarning}
                                </div>
                            )}

                            <div className="border-t border-theme"></div>

                            {/* Manual sync retries a dead grant. Reconnect on the row is the step. */}
                            {!everyConnectedGrantExpired && (
                            <form onSubmit={handleSync} className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-theme font-semibold text-sm">Manual Sync</p>
                                    {syncStatus === 'COMPLETED' && lastSyncedAt && (
                                        <span className="text-green-400 text-xs">
                                            {/* parseMailboxStatusDate-based: naive backend timestamps are UTC,
                                                so this renders the same local instant as the mailbox cards. */}
                                            Last: {formatLastSuccessfulSync(lastSyncedAt)}
                                        </span>
                                    )}
                                </div>

                                {connectedMailboxes.length > 1 && (
                                    <div>
                                        <label
                                            htmlFor="manual-sync-mailbox"
                                            className="block text-xs text-theme-muted mb-1"
                                        >
                                            Mailbox
                                        </label>
                                        <select
                                            id="manual-sync-mailbox"
                                            value={manualMailboxId ?? ''}
                                            onChange={(e) => setManualMailboxId(Number(e.target.value))}
                                            disabled={!hasAnyConnected}
                                            className="w-full bg-theme-secondary border border-theme rounded-lg px-4 py-2 text-theme text-sm focus:border-electric-blue outline-none transition-colors disabled:opacity-50"
                                        >
                                            {connectedMailboxes.map((mailbox) => (
                                                <option key={mailbox.id} value={mailbox.id}>
                                                    {mailbox.email_address}
                                                    {mailbox.is_primary ? ' (primary)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {manualQuerySupported ? (
                                    <div>
                                        <label className="block text-xs text-theme-muted mb-1">Search Query (Optional)</label>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder='e.g. from:greenhouse.io subject:application'
                                            disabled={!hasAnyConnected || manualSyncBlocked}
                                            className="w-full bg-theme-secondary border border-theme rounded-lg px-4 py-2 text-theme text-sm focus:border-electric-blue outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <p className="text-xs text-theme-muted mt-1">
                                            Uses Gmail search syntax. Leave blank for default inbox sync.
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-xs text-theme-muted">
                                        Search queries aren&apos;t available for this mailbox — it syncs
                                        through a managed email provider. Use the date range below to
                                        narrow a sync.
                                    </p>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-theme-muted mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            disabled={!hasAnyConnected || manualSyncBlocked}
                                            className="w-full bg-theme-secondary border border-theme rounded-lg px-4 py-2 text-theme text-sm focus:border-electric-blue outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-theme-muted mb-1">End Date</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            disabled={!hasAnyConnected || manualSyncBlocked}
                                            className="w-full bg-theme-secondary border border-theme rounded-lg px-4 py-2 text-theme text-sm focus:border-electric-blue outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={!hasAnyConnected || manualSyncBlocked}
                                    className={`w-full font-bold py-3 rounded-xl shadow-neon transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none ${syncStatus === 'FAILED' ? 'bg-red-500 hover:bg-red-600' : 'bg-electric-blue hover:bg-blue-600'
                                        } text-white`}
                                >
                                    {manualSyncBlocked ? (
                                        <>
                                            <FontAwesomeIcon icon={faSync} spin />
                                            Syncing...
                                        </>
                                    ) : syncStatus === 'FAILED' ? (
                                        <>
                                            <FontAwesomeIcon icon={faSync} />
                                            Retry Sync
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSync} />
                                            Start Sync
                                        </>
                                    )}
                                </button>

                                {!hasAnyConnected && (
                                    <p className="text-xs text-theme-muted text-center">
                                        Connect a Gmail account above to enable syncing
                                    </p>
                                )}
                            </form>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConnectGmailModal;
